import { postJson } from "./http";
import type { LedgerImportIssue, LedgerPushDTO } from "./ledgerImportApi";

/**
 * The No Bill ledgers: one folder in the client's chart, a child per category.
 *
 * Shares the push machinery with the sheet import — same staging tables, same
 * batched writer — so `LedgerPushDTO` is imported rather than restated. What is
 * different is the input: a list of category names off a set of ticks, not a
 * file, so there is no upload here and no duplicate-file gate.
 */

/** An account carrying the No Bill name that is already in the client's books. */
export interface NoBillExistingDTO {
  account_id: string;
  account_name: string;
  /** The name without the `No Bill - ` prefix — what the tick list shows. */
  label: string;
  /**
   * It hangs off the folder. False for one somebody created at the top level of
   * the chart instead, which is still an account whose name is taken.
   */
  under_parent: boolean;
  active: boolean;
}

/**
 * One of the client's own expense ledgers, offered as a category to tick.
 *
 * A name to copy, not a link. Ticking it creates `No Bill - {label}` beside the
 * original and leaves the original alone — the billed ledger keeps its history,
 * the undocumented spend gets its own account.
 */
export interface NoBillCandidateDTO {
  account_id: string;
  label: string;
  account_type: string;
  /** The `No Bill - ` twin is already in the books, so ticking creates nothing. */
  exists: boolean;
}

export interface NoBillParentDTO {
  name: string;
  account_id: string | null;
  exists: boolean;
  /** Marked inactive in Zoho, which means it cannot take children until it is not. */
  inactive: boolean;
}

/** One staged row: the folder, or a category waiting to be created. */
export interface NoBillRowDTO {
  id: number;
  account_name: string | null;
  label: string;
  is_parent: boolean;
  action: "new" | "update" | "unchanged" | "invalid";
  status: "pending" | "created" | "updated" | "skipped" | "failed";
  issues: LedgerImportIssue[];
  error: string | null;
}

/**
 * A ledger the client already uses for spend without a bill.
 *
 * `account_name` is what it was called when somebody decided that; `current_name`
 * is what Zoho calls it now. They differ when it has been renamed since, which is
 * reported rather than smoothed over — showing the new name as though it were the
 * agreed one would quietly rewrite the decision.
 */
export interface NoBillMappedDTO {
  account_id: string;
  account_name: string;
  current_name: string | null;
  renamed: boolean;
  /** Gone from the chart of accounts entirely. */
  missing: boolean;
  active: boolean;
  mapped_at: string | null;
}

export interface NoBillImportDTO {
  id: number;
  status: "validated" | "committing" | "done" | "cancelled" | "failed";
  total_rows: number;
  new_rows: number;
  invalid_rows: number;
  created_rows: number;
  failed_rows: number;
  created_at: string | null;
  committed_at: string | null;
}

export interface NoBillStateDTO {
  /** False before the chart of accounts has ever been read, when nothing here can work. */
  chart_read: boolean;
  parent: NoBillParentDTO;
  existing: NoBillExistingDTO[];
  /** The client's own expense ledgers, alphabetical. Empty if none were read. */
  candidates: NoBillCandidateDTO[];
  /** Ledgers of theirs recorded as no-bill ledgers. Never sent to Zoho. */
  mapped: NoBillMappedDTO[];
  /** Null whenever nothing is staged, which is the ordinary state. */
  import: NoBillImportDTO | null;
  rows: NoBillRowDTO[];
  push?: LedgerPushDTO;
}

interface StateEnvelope {
  success: boolean;
  message: string;
  data: NoBillStateDTO;
}

// The readable reason arrives under `errors` — "that is 80 categories" or
// "the folder is marked inactive in Zoho" — and `message` is only ever
// "Validation failed" behind it.
const noBillCall = <T>(path: string, body: unknown): Promise<T> =>
  postJson<T>(path, body, ["labels", "account_ids", "commit", "public_id"]);

/** What is in the books, what is staged, and how the last push went. */
export const fetchNoBillState = async (publicId: string): Promise<NoBillStateDTO> =>
  (await noBillCall<StateEnvelope>("/api/onboarding/ledgers/no-bill/get", { public_id: publicId })).data;

/**
 * Work out what these categories would add, and stage it. Nothing reaches Zoho.
 *
 * Re-runnable: staging again replaces whatever was staged and not yet pushed.
 * An empty list is meaningful rather than a mistake — it stages the folder alone,
 * for a client who wants one bucket instead of a breakdown.
 */
export const stageNoBillLedgers = async (publicId: string, labels: string[]): Promise<NoBillStateDTO> =>
  (await noBillCall<StateEnvelope>("/api/onboarding/ledgers/no-bill/stage", { public_id: publicId, labels })).data;

/**
 * Record which of the client's own ledgers are their no-bill ledgers.
 *
 * LOCAL ONLY. Nothing here reaches Zoho, now or on any later push — the ledgers
 * keep their names, their parents and their history. Replaces the stored set, so
 * an id left out is a ledger the CA is saying is not one of these.
 */
export const saveNoBillMapping = async (publicId: string, accountIds: string[]): Promise<NoBillStateDTO> =>
  (
    await noBillCall<StateEnvelope>("/api/onboarding/ledgers/no-bill/mapping", {
      public_id: publicId,
      account_ids: accountIds,
    })
  ).data;

/** Drop a staged set. On a finished push this dismisses the outcome instead. */
export const cancelNoBillLedgers = async (publicId: string): Promise<NoBillStateDTO> =>
  (await noBillCall<StateEnvelope>("/api/onboarding/ledgers/no-bill/cancel", { public_id: publicId })).data;

/**
 * Create one batch of the staged ledgers in the client's Zoho Books.
 *
 * THIS WRITES TO A CUSTOMER'S BOOKS AND NOTHING IT DOES CAN BE UNDONE. The caller
 * keeps calling while `push.status` is `committing`, and stops on `push.halted`,
 * which means calling again now would fail the same way.
 */
export const commitNoBillLedgers = async (publicId: string): Promise<NoBillStateDTO> =>
  (await noBillCall<StateEnvelope>("/api/onboarding/ledgers/no-bill/commit", { public_id: publicId })).data;

/** Dismiss a finished push. Not an undo — the ledgers are in Zoho. */
export const closeNoBillLedgers = async (publicId: string): Promise<NoBillStateDTO> =>
  (await noBillCall<StateEnvelope>("/api/onboarding/ledgers/no-bill/close", { public_id: publicId })).data;
